import { useEffect, useState } from 'react';
import { api, type ReqAccountKind } from '../lib/api';
import { DataTable, ErrorBox, Pager, usePageSize, dash, money, useList, type Column, type Row, IdCell, MoneyInput } from '../components/DataTable';
import { canScreen, useMe } from '../lib/me';
import DriveMonPicker from '../components/DriveMonPicker';
import { pickDriveMon, useStickyDriveMon } from '../lib/driveMon';
import Modal from '../components/Modal';
import { useAutoSearch } from '../lib/search';
import MonthSearchHint from '../components/MonthSearchHint';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { link, urlParam, useUrlSearch } from '../lib/deepLink';
import { AllocChangeBanner } from '../components/AllocChangeAlerts';
import SearchSelect from '../components/SearchSelect';

/**
 * 영업정산 — 배정(배차×차량) 한 건에 붙는 **네 갈래 가감**.
 *
 * 한 행이 정산 두 도메인에 동시에 흘러든다:
 *   · 거래처 청구 = 운행료 + Σ청구 − Σ공제
 *   · 기사 명세  = `Req` 지급/공제 줄
 * 그래서 화면도 두 축을 나란히 보여준다 — 한쪽만 보면 반대쪽이 왜 바뀌는지 모른다.
 *
 * ⚠️ **'선지급' 공제 행은 관리자만 고칠 수 있다**(레거시 `usp_ResultReq_Update` 가 `busman` 외에는 -1).
 *    실측 1,628행이 여기 걸린다. 잠긴 행은 자물쇠로 표시한다 — 눌렀다가 거절당하지 않게.
 *
 * ⚠️ **마감된 달도 고칠 수 있다.** 레거시 SP 에 게이트가 없다(재무추가와 같은 판단).
 *    대신 마감 상태를 띄운다 — 고치면 이미 만든 명세·청구와 어긋난다.
 */


const KIND_LABEL: Record<ReqAccountKind, string> = {
  거래처청구: '거래처 청구(+)',
  거래처공제: '거래처 공제(−)',
  기사지급: '기사 지급(+)',
  기사공제: '기사 공제(−)',
  수익비용: '수익 비용(지급 아님)',
};

/**
 * 수익 비용 계정 — 목록에 없으면 직접 쳐서 새 계정으로(행사 지원비·단말기 사용료… 종류가 계속 생긴다).
 * 서버가 저장할 때 마스터에 더하므로 다음부터 목록에 뜬다. 오타로 계정이 갈라지지 않게 새 이름이면 한 줄 알린다.
 */
function ProfitCostAccountField({
  value, onChange, disabled,
}: { value: string; onChange: (v: string) => void; disabled?: boolean }) {
  const [opts, setOpts] = useState<string[] | null>(null);
  useEffect(() => {
    let alive = true;
    api.listReqAccountNames({ kind: '수익비용' })
      .then((r) => { if (alive) setOpts(r.rows.map((x) => x.account_name)); })
      .catch(() => undefined);
    return () => { alive = false; };
  }, []);
  const name = value.trim();
  const isNew = opts !== null && name !== '' && !opts.includes(name);
  return (
    <div>
      <input className="field" list="req-profit-cost-accounts" value={value} placeholder="계정 고르기 또는 직접 입력"
        onChange={(e) => onChange(e.target.value)} disabled={disabled} />
      <datalist id="req-profit-cost-accounts">
        {(opts ?? []).map((o) => <option key={o} value={o} />)}
      </datalist>
      {isNew && <p className="mt-1 text-xs text-amber-700">목록에 없는 이름입니다 — 저장하면 새 계정으로 등록됩니다.</p>}
    </div>
  );
}

/**
 * 계정 선택 — 축마다 목록이 다르다. 자유입력도 허용한다(과거 데이터에 마스터 밖 값이 있다).
 * 거래처 청구 상세의 **일괄 등록**도 이걸 쓴다 — 계정 마스터 축이 두 벌이 되면 안 된다.
 */
export function AccountField({
  kind, value, onChange, disabled,
}: { kind: ReqAccountKind; value: string; onChange: (v: string) => void; disabled?: boolean }) {
  const [opts, setOpts] = useState<string[]>([]);
  useEffect(() => {
    let alive = true;
    api.listReqAccountNames({ kind })
      .then((r) => { if (alive) setOpts(r.rows.map((x) => x.account_name)); })
      .catch(() => undefined);
    return () => { alive = false; };
  }, [kind]);

  // 쳐서 찾기(#439) — 마스터에 없는 과거 값은 칸에 그대로 남는다(손대지 않으면 저장 때 지워지지 않는다).
  return (
    <SearchSelect value={value} options={opts} onChange={onChange} disabled={disabled}
                  placeholder="계정 선택 · 쳐서 찾기" extraLabel="⚠ 미등록" />
  );
}

/**
 * 영업정산을 붙일 대상 — 배정 번호가 있으면 그것, 통근 달 줄(배정 번호 없음)은 노선·기사·차량(#77·#80).
 * 서버(`resultReqWrite.ts resolveTargets`)가 그 달 줄이 실제로 있는지 보고 나머지 번호를 편다.
 */
export type ReqTarget = { disptCarSchId?: number; disptId?: number; driverId?: number | null; carId?: number | null };
export function reqTargetOf(r: Row): ReqTarget | null {
  if (r.dispt_car_sch_id) return { disptCarSchId: Number(r.dispt_car_sch_id) };
  if (r.dispt_id && (r.driver_id != null || r.car_id != null)) {
    return { disptId: Number(r.dispt_id), driverId: r.driver_id == null ? null : Number(r.driver_id), carId: r.car_id == null ? null : Number(r.car_id) };
  }
  return null;
}
export const reqKeyOf = (r: Row) => (r.dispt_car_sch_id ? `s${r.dispt_car_sch_id}` : `a${r.dispt_id}|${r.driver_id ?? ''}|${r.car_id ?? ''}`);

/**
 * 이 정산월에 **실제로 운행한 줄**에서 찾기(기본 · #80). 「배차 찾기」는 노선을 출발시각 최신순 20건으로 보여 줘
 * 시작일이 오래된 통근 노선이 흔한 검색어에선 목록에 못 올라왔다.
 */
function MonthPicker({ mon, onPick }: { mon: string; onPick: (t: ReqTarget, label: string) => void }) {
  const [term, setTerm] = useState('');
  const [rows, setRows] = useState<Row[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  async function run(raw: string) {
    if (!/^\d{4}-\d{2}$/.test(mon)) { setErr('정산월을 먼저 고르세요.'); return; }
    setBusy(true); setErr(null);
    try {
      const r = await api.listReqTargets({ driveMon: mon, q: raw.trim() || undefined });
      setRows(r.rows as unknown as Row[]);
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); } finally { setBusy(false); }
  }
  useAutoSearch(term, (v) => { if (v !== '') void run(v); });
  return (
    <div className="space-y-2">
      <form onSubmit={(e) => { e.preventDefault(); void run(term); }} className="flex items-center gap-2">
        <input className="field w-72" placeholder="거래처·노선·기사·차량번호·노선 NO" value={term} onChange={(e) => setTerm(e.target.value)} />
        <button className="btn-ghost" type="submit" disabled={busy}>찾기</button>
        <span className="text-xs text-ink-faint">{mon} 운행분</span>
      </form>
      {err && <p className="text-xs text-red-600">{err}</p>}
      {rows.length > 0 && (
        <ul className="max-h-56 space-y-0.5 overflow-y-auto text-sm">
          {rows.map((r) => {
            const t = reqTargetOf(r);
            const label = `${dash(r.car_no)} · ${dash(r.driver_name)} · ${dash(r.dispt_name)}`;
            return (
              <li key={reqKeyOf(r)}>
                <button type="button" className="w-full rounded px-2 py-1 text-left hover:bg-surface" disabled={!t}
                  onClick={() => t && onPick(t, label)}>
                  <span className="mr-1 text-xs text-ink-faint">NO {dash(r.dispt_id)}</span>
                  <span className="text-ink-faint">{dash(r.recv_type)}</span> {dash(r.cust_name)} · {dash(r.dispt_name)}
                  <span className="ml-2 font-medium">{dash(r.driver_name)}</span>
                  <span className="ml-1 text-xs text-ink-soft">{dash(r.car_no)}</span>
                  {r.commute ? <span className="ml-2 text-xs text-ink-faint">운행 {dash(r.cnt)}일</span> : null}
                </button>
              </li>
            );
          })}
        </ul>
      )}
      {!busy && term.trim() !== '' && rows.length === 0 && !err && (
        <p className="text-xs text-ink-faint">{mon} 에 운행한 줄 중 맞는 것이 없습니다. 통근은 「통근 운행일수」에서 그 달 줄을 만들어야 나옵니다.</p>
      )}
    </div>
  );
}

/** 신규 등록의 대상 고르기 — 배차 → 그 배차의 배정(차량). 서버가 이 배정에서 6개 ID 를 편다. */
function SchPicker({ onPick }: { onPick: (t: ReqTarget, label: string) => void }) {
  const [term, setTerm] = useState('');
  const [dispts, setDispts] = useState<Row[]>([]);
  const [disptId, setDisptId] = useState<number | null>(null);
  const [schs, setSchs] = useState<Row[]>([]);
  const [busy, setBusy] = useState(false);

  /** 실제 조회. 버튼·Enter 와 자동검색이 **같은 경로**를 쓴다(둘이 갈리면 한쪽만 고쳐진다). */
  async function run(raw: string) {
    setBusy(true); setDisptId(null); setSchs([]);
    try {
      const r = await api.listDispts({ q: raw.trim() || undefined, limit: 20 });
      setDispts(r.rows as unknown as Row[]);
    } finally { setBusy(false); }
  }

  function search(e: React.FormEvent) {
    e.preventDefault();
    void run(term);
  }

  // 치는 대로 찾는다(입력이 멎으면 적용). 배차는 모수가 커서 **빈 검색어로는 안 부른다.**
  useAutoSearch(term, (v) => { if (v !== '') void run(v); });

  async function open(d: Row) {
    setDisptId(Number(d.dispt_id));
    setBusy(true);
    try {
      const r = await api.listDisptCarSchs({ disptId: Number(d.dispt_id), limit: 50 });
      setSchs(r.rows as unknown as Row[]);
    } finally { setBusy(false); }
  }

  return (
    <div className="space-y-2 rounded-lg border border-line bg-canvas p-3">
      <form onSubmit={search} className="flex items-center gap-2">
        <input
          className="field w-64"
          placeholder="배차명·거래처·NO로 검색"
          value={term}
          onChange={(e) => setTerm(e.target.value)}
        />
        <button className="btn-ghost" type="submit" disabled={busy}>배차 찾기</button>
      </form>

      {dispts.length > 0 && (
        <ul className="max-h-40 space-y-1 overflow-y-auto text-sm">
          {dispts.map((d) => (
            <li key={String(d.dispt_id)}>
              <button
                type="button"
                className={`w-full rounded px-2 py-1 text-left hover:bg-surface ${
                  disptId === Number(d.dispt_id) ? 'bg-brand-50' : ''
                }`}
                onClick={() => void open(d)}
              >
                <span className="mr-1 text-xs text-ink-faint">NO {dash(d.dispt_id)}</span>
                <span className="text-ink-faint">{dash(d.recv_type)}</span> {dash(d.cust_name)} · {dash(d.dispt_name)}
                <span className="ml-2 text-xs text-ink-faint">{dash(d.start_time)}</span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {disptId !== null && (
        schs.length === 0
          ? <p className="text-xs text-ink-faint">{busy ? '불러오는 중…' : '이 배차에는 배정된 차량이 없습니다. 먼저 차량을 배정하세요.'}</p>
          : (
            <ul className="flex flex-wrap gap-2">
              {schs.map((s) => (
                <li key={String(s.dispt_car_sch_id)}>
                  <button
                    type="button"
                    className="btn-ghost text-xs"
                    onClick={() => onPick(
                      { disptCarSchId: Number(s.dispt_car_sch_id) },
                      `${dash(s.car_no)} · ${dash(s.driver_name)} (${dash(s.owner_name)})`,
                    )}
                  >
                    {dash(s.car_no)} · {dash(s.driver_name)}
                  </button>
                </li>
              ))}
            </ul>
          )
      )}
    </div>
  );
}

/**
 * 영업정산 등록·수정 폼.
 *
 * **거래처 청구 상세**(`CustBillDetail`)도 이걸 쓴다 — 기사 수당·공제가 곧 지출계라
 * 거래처 수익을 보면서 넣는 것이 원래 한 작업이다(레거시도 거래처정산에서 같이 했다).
 * 폼을 두 벌 두면 계정 마스터·마감 게이트·선지급 잠금이 한쪽에만 붙는다.
 */
export function ReqForm({
  row, driveMon, isAdmin, onClose, onSaved, preset,
}: {
  row: Row | 'new';
  driveMon: string;
  isAdmin: boolean;
  onClose: () => void;
  onSaved: () => void;
  /**
   * 대상 배정을 미리 정해서 연다. 청구 상세는 **그 차량 줄을 이미 알고** 있으므로
   * 거기서 다시 찾게 하지 않는다 — 찾는 단계가 곧 "화면을 옮겨 다니는" 그 비용이다.
   */
  preset?: { target: ReqTarget; label: string };
}) {
  const editing = row !== 'new';
  const r = editing ? row : null;

  const [schId, setSchId] = useState<ReqTarget | null>(preset?.target ?? null);
  const [pickBy, setPickBy] = useState<'month' | 'dispt'>('month');
  const [schLabel, setSchLabel] = useState(preset?.label ?? '');
  const [mon, setMon] = useState(String(r?.drive_mon ?? driveMon ?? ''));
  const [v, setV] = useState({
    custPlus: String(r?.cust_plus ?? ''), custMinus: String(r?.cust_minus ?? ''),
    driverPlus: String(r?.driver_plus ?? ''), driverMinus: String(r?.driver_minus ?? ''),
    custPlusAccount: String(r?.cust_plus_account ?? ''), custMinusAccount: String(r?.cust_minus_account ?? ''),
    driverPlusAccount: String(r?.driver_plus_account ?? ''), driverMinusAccount: String(r?.driver_minus_account ?? ''),
    companyCost: Number(r?.company_cost ?? 0) ? String(r?.company_cost) : '',
    companyCostAccount: String(r?.company_cost_account ?? ''),
    comment: String(r?.comment ?? ''),
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const locked = editing && r?.is_locked === true && !isAdmin;
  const set = (k: keyof typeof v, val: string) => setV((c) => ({ ...c, [k]: val }));

  async function save() {
    setSaving(true); setError(null);
    try {
      await api.saveResultReq({
        resultReqId: editing ? Number(r!.result_req_id) : undefined,
        ...(editing ? {} : (schId ?? {})),
        driveMon: mon,
        ...v,
      });
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setSaving(false);
    }
  }

  const AMOUNTS: [ReqAccountKind, keyof typeof v, keyof typeof v, string][] = [
    ['거래처청구', 'custPlus', 'custPlusAccount', 'text-emerald-700'],
    ['거래처공제', 'custMinus', 'custMinusAccount', 'text-red-700'],
    ['기사지급', 'driverPlus', 'driverPlusAccount', 'text-emerald-700'],
    ['기사공제', 'driverMinus', 'driverMinusAccount', 'text-red-700'],
  ];

  return (
    <div className="card space-y-4 p-5">
      <div className="btn-row-sm flex flex-wrap items-center gap-3">
        <h2 className="text-base font-bold">영업정산 {editing ? '수정' : '등록'}</h2>
        {editing && (
          <span className="text-sm text-ink-soft">
            {dash(r!.cust_name)} · {dash(r!.dispt_name)} · {dash(r!.car_no)} {dash(r!.driver_name)}
          </span>
        )}
        <button className="btn-ghost ml-auto" onClick={onClose} disabled={saving}>취소</button>
        <button className="btn-primary" onClick={save} disabled={saving || locked || (!editing && schId === null)}>
          {saving ? '저장 중…' : editing ? '수정' : '등록'}
        </button>
      </div>

      {locked && (
        <p className="rounded bg-amber-50 px-3 py-2 text-sm text-amber-800">
          이 행은 <b>선지급</b> 공제라 관리자만 고칠 수 있습니다.
        </p>
      )}
      {error && <ErrorBox message={error} />}

      {/* 미리 정해서 열었으면 고르기 칸을 아예 안 보여준다 — 이미 정해진 것을 다시 묻지 않는다. */}
      {!editing && preset && (
        <p className="rounded-lg bg-canvas px-3 py-2 text-sm">
          <span className="text-ink-soft">대상 배정</span> <b>{preset.label}</b>
        </p>
      )}

      {!editing && !preset && (
        <div className="space-y-1">
          <span className="text-sm font-medium">대상 배정 {schId === null && <span className="text-red-600">*</span>}</span>
          {schId === null
            ? (
              <div className="space-y-2 rounded-lg border border-line bg-canvas p-3">
                <div className="flex gap-1 text-xs">
                  <button type="button" className={pickBy === 'month' ? 'btn-primary px-2 py-0.5 text-xs' : 'btn-ghost px-2 py-0.5 text-xs'} onClick={() => setPickBy('month')}>이 정산월 운행에서 찾기</button>
                  <button type="button" className={pickBy === 'dispt' ? 'btn-primary px-2 py-0.5 text-xs' : 'btn-ghost px-2 py-0.5 text-xs'} onClick={() => setPickBy('dispt')}>배차에서 찾기</button>
                </div>
                {pickBy === 'month'
                  ? <MonthPicker mon={mon} onPick={(t, label) => { setSchId(t); setSchLabel(label); }} />
                  : <SchPicker onPick={(t, label) => { setSchId(t); setSchLabel(label); }} />}
              </div>
            )
            : (
              <p className="text-sm">
                {schLabel}
                <button className="btn-ghost ml-2 text-xs" onClick={() => { setSchId(null); setSchLabel(''); }}>
                  다시 고르기
                </button>
              </p>
            )}
        </div>
      )}

      <label className="block w-40">
        <span className="mb-1 block text-sm font-medium">정산월</span>
        <input className="field" value={mon} onChange={(e) => setMon(e.target.value)} placeholder="YYYY-MM" />
      </label>

      <div className="grid gap-4 md:grid-cols-2">
        {AMOUNTS.map(([kind, amtKey, accKey, tone]) => (
          <div key={kind} className="space-y-1 rounded-lg border border-line p-3">
            <span className={`text-sm font-medium ${tone}`}>{KIND_LABEL[kind]}</span>
            <MoneyInput placeholder="0" value={v[amtKey]} onChange={(x) => set(amtKey, x)} disabled={locked} />
            <AccountField kind={kind} value={v[accKey]} onChange={(x) => set(accKey, x)} disabled={locked} />
          </div>
        ))}
        <div className="space-y-1 rounded-lg border border-amber-300 p-3 md:col-span-2">
          <span className="text-sm font-medium text-amber-700">{KIND_LABEL['수익비용']}</span>
          <p className="text-xs text-ink-soft">기사에게 지급되지 않고 거래처 수익 계산에서만 빠지는 비용입니다(행사 관리직 지원비·단말기 사용료 등). 기사 편성 없이 총액만 넣습니다.</p>
          <div className="grid gap-2 md:grid-cols-2">
            <MoneyInput placeholder="0" value={v.companyCost} onChange={(x) => set('companyCost', x)} disabled={locked} />
            <ProfitCostAccountField value={v.companyCostAccount} onChange={(x) => set('companyCostAccount', x)} disabled={locked} />
          </div>
        </div>
      </div>

      <label className="block">
        <span className="mb-1 block text-sm font-medium">비고{Number(v.companyCost || 0) ? ' (근거: 인원·명단·단가·총액 등)' : ''}</span>
        <input className="field" value={v.comment} onChange={(e) => set('comment', e.target.value)} disabled={locked}
          placeholder={Number(v.companyCost || 0) ? '예: 관리직 10명(○○ 팀장 외) 1인 80만 × 10 = 800만' : undefined} />
      </label>
    </div>
  );
}

export default function ResultReq() {
  const { me } = useMe();
  const canEdit = canScreen(me, 'result-req');
  const isAdmin = me?.role === 'admin';

  const [months, setMonths] = useState<string[]>([]);
  // 고른 정산년월은 화면을 옮겨도 남는다 — 담당자는 한 달을 여러 화면에서 처리한다.
  const [mon, setMon] = useStickyDriveMon();
  const [recvType, setRecvType] = useState('');
  const [term, setTerm] = useState('');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(0);
  const [PAGE, setPAGE] = usePageSize('result-req', 50);

  // 치는 대로 찾는다(입력이 멎으면 적용). 버튼·Enter 는 그대로 — `lib/search.ts` 참고.
  useAutoSearch(term, (v) => { setQ(v); setPage(0); });
  // 다른 화면에서 건너온 링크(`?id=<영업정산 NO>&mon=` — 출납일보 DY 뱃지·OPS 탭·정산현황 줄 보기 · 2026-10-02).
  // `id` 가 있으면 그 줄 하나만 보여 준다(정산월·구분·검색과 무관) — 「풀기」로 평소 목록.
  const { search } = useLocation();
  const navigate = useNavigate();
  const pinRaw = urlParam(search, 'id');
  const pinId = pinRaw && /^\d+$/.test(pinRaw) ? Number(pinRaw) : null;
  useUrlSearch(setTerm, setQ, setMon);
  const [nonce, setNonce] = useState(0);
  const [editing, setEditing] = useState<Row | 'new' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sum, setSum] = useState<
    { n: string; cust_plus: string; cust_minus: string; driver_plus: string; driver_minus: string } | null
  >(null);
  const [finished, setFinished] = useState<string[]>([]);

  useEffect(() => {
    api.listDriveMonths()
      .then((r) => { setMonths(r.rows); setMon(pickDriveMon(r.rows, mon)); })
      .catch(() => undefined);
    // 마운트 때 한 번만 — `mon` 은 기억된 값을 읽는 용도다(의존성에 넣으면 월마다 재조회).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { rows, loading } = useList(async (alive) => {
    const r = await api.listResultReq(pinId
      ? { resultReqId: pinId, limit: PAGE, offset: 0 }
      : {
          driveMon: mon || undefined,
          recvType: recvType || undefined,
          q: q || undefined,
          limit: PAGE,
          offset: page * PAGE,
        });
    if (alive()) {
      setSum(r.total);
      setFinished(r.finishedTypes);
    }
    return r as unknown as { rows: Row[] };
  }, [mon, recvType, q, page, PAGE, nonce, pinId]);

  async function remove(r: Row) {
    if (!window.confirm(`${dash(r.car_no)} ${dash(r.driver_name)} 의 영업정산을 삭제할까요?`)) return;
    setError(null);
    try {
      await api.deleteResultReq({ resultReqId: Number(r.result_req_id) });
      setNonce((n) => n + 1);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  const columns: Column[] = [
    // 영업정산 한 줄 = 한 노선의 한 배정. 접수·노선 NO 는 배차 화면의 NO 와 같은 값이다(레거시 RecvId·DisptId).
    // phone: 폰에선 거래처를 왼쪽에 고정하고 번호·ID·차주 칸은 뺀다(DataTable `phone` · 2026-09-30 way).
    { key: 'recv_id', label: '접수 NO', align: 'right', phone: 'hide', render: (r) => <span className="text-xs text-ink-soft">{dash(r.recv_id)}</span> },
    { key: 'dispt_id', label: '노선 NO', align: 'right', phone: 'hide', render: (r) => <span className="text-xs text-ink-soft">{dash(r.dispt_id)}</span> },
    {
      key: 'recv_type',
      label: '구분',
      render: (r) => (
        <span className={`rounded px-1.5 py-0.5 text-xs font-medium ${
          r.recv_type === '통근' ? 'bg-sky-50 text-sky-700' : 'bg-canvas text-ink-soft'
        }`}>
          {dash(r.recv_type)}
        </span>
      ),
    },
    { key: 'cust_name', label: '거래처', phone: 'pin' },
    { key: 'dispt_name', label: '배차' },
    { key: 'car_alloc_id', label: '편성 ID', align: 'right', phone: 'hide', render: (r) => <IdCell v={r.car_alloc_id} warn /> },
    { key: 'driver_id', label: '기사 ID', align: 'right', phone: 'hide', render: (r) => <IdCell v={r.driver_id} /> },
    { key: 'owner_id', label: '차주 ID', align: 'right', phone: 'hide', render: (r) => <IdCell v={r.owner_id} /> },
    { key: 'owner_name', label: '차주', phone: 'hide' },
    { key: 'driver_name', label: '기사' },
    { key: 'car_no', label: '차량' },
    {
      key: 'cust_net',
      label: '거래처 증감',
      align: 'right',
      render: (r) => <span className={Number(r.cust_net) < 0 ? 'text-red-700' : ''}>{money(r.cust_net)}</span>,
    },
    {
      key: 'driver_net',
      label: '기사 증감',
      align: 'right',
      render: (r) => <span className={Number(r.driver_net) < 0 ? 'text-red-700' : ''}>{money(r.driver_net)}</span>,
    },
    {
      key: '_acc',
      label: '계정',
      render: (r) => (
        <span className="text-xs text-ink-soft">
          {[r.cust_plus_account, r.cust_minus_account, r.driver_plus_account, r.driver_minus_account]
            .filter(Boolean).join(' · ') || (Number(r.company_cost ?? 0) ? '' : '—')}
          {Number(r.company_cost ?? 0) !== 0 && (
            <span className="ml-1 rounded bg-amber-50 px-1 text-amber-800" title="수익 비용(지급 아님) — 급여로 안 가고 거래처 수익에서만 빠집니다(손입력 또는 DYOPS 대중교통비 자동 반영).">
              {String(r.company_cost_account ?? '회사부담')} {money(r.company_cost)}
            </span>
          )}
          {r.is_locked === true && <span className="ml-1" title="선지급 — 관리자만 수정">🔒</span>}
        </span>
      ),
    },
    {
      // OPS 대중교통비·선지급이 만든 줄(또는 비고에 DY 번호) — 출납 줄·OPS 탭으로(2026-10-02 · 월보구 증빙).
      key: 'ops_dy_no',
      label: 'OPS',
      render: (r) => (r.ops_dy_no ? (
        <span className="flex flex-wrap items-center gap-1 text-[11px]" onClick={(e) => e.stopPropagation()}>
          <Link className="rounded bg-violet-100 px-1 font-mono text-violet-800 hover:underline" to={link.opsTransit(String(r.ops_dy_no))}
                title={r.ops_linked ? 'OPS 자동 반영 줄 — OPS 탭에서 보기' : '비고에 DY 번호가 있는 줄 — OPS 탭에서 보기'}>
            {String(r.ops_dy_no)}
          </Link>
          {r.ops_cash_book_id ? (
            <Link className="rounded border border-line px-1 text-brand-700 hover:bg-brand-50"
                  to={link.cashBook(String(r.ops_cash_book_id), r.ops_cash_date ? String(r.ops_cash_date) : null)} title="출납일보에서 이 돈이 나간 줄 보기">
              출납 {dash(r.ops_cash_date)}
            </Link>
          ) : null}
        </span>
      ) : <span className="text-ink-faint">—</span>),
    },
    ...(canEdit
      ? [{
          key: '_del',
          label: '',
          render: (r: Row) => (
            <button
              className="btn-ghost py-0.5 text-xs text-red-600"
              onClick={(e) => { e.stopPropagation(); void remove(r); }}
            >
              삭제
            </button>
          ),
        } as Column]
      : []),
  ];

  return (
    <div className="space-y-4">
      {/* 운행편성 변경(10-02) — 이 정산월에 편성이 바뀐 차량이 있으면 늘 보인다 */}
      <AllocChangeBanner mon={mon} />
      <div className="page-head">
        <h1 className="font-bold">영업정산</h1>
        <p className="mt-1 text-sm text-ink-soft">
          운행 한 건에 붙는 <b>추가 청구·공제</b>입니다. 거래처 청구액과 기사 명세에 <b>동시에</b> 반영됩니다.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <DriveMonPicker
          months={months}
          value={mon}
          onChange={(m) => { setMon(m); setPage(0); setEditing(null); }}
        />

        <div className="flex gap-1">
          {([['', '전체'], ['일반', '일반'], ['통근', '통근']] as const).map(([id, label]) => (
            <button
              key={id}
              onClick={() => { setRecvType(id); setPage(0); }}
              className={`rounded-lg px-3 py-1.5 text-sm font-medium ${
                recvType === id ? 'bg-brand-600 font-semibold text-white shadow-sm' : 'text-ink hover:bg-brand-50'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        <form onSubmit={(e) => { e.preventDefault(); setQ(term.trim()); setPage(0); }} className="flex items-center gap-2">
          <input
            className="field w-56"
            placeholder="거래처·배차·기사·차량·차주·NO"
            value={term}
            onChange={(e) => setTerm(e.target.value)}
          />
          <button className="btn-search" type="submit" aria-label="검색" title="검색" />
        </form>

        {canEdit && (
          <button className="btn ml-auto" onClick={() => setEditing('new')}>새 항목</button>
        )}
      </div>

      {sum && (
        <div className="flex flex-wrap items-center gap-4 rounded-lg border border-line bg-surface px-3 py-2 text-sm">
          <span className="text-ink-soft">{Number(sum.n).toLocaleString('ko-KR')}건</span>
          <span>거래처 청구 <b className="text-emerald-700">{money(sum.cust_plus)}</b></span>
          <span>거래처 공제 <b className="text-red-700">{money(sum.cust_minus)}</b></span>
          <span>기사 지급 <b className="text-emerald-700">{money(sum.driver_plus)}</b></span>
          <span>기사 공제 <b className="text-red-700">{money(sum.driver_minus)}</b></span>
          {finished.length > 0 && (
            <span className="rounded bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-800">
              이 달 마감됨({finished.join('·')}) — 고치면 이미 만든 청구·명세와 어긋납니다
            </span>
          )}
        </div>
      )}

      {pinId !== null && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-1.5 text-sm">
          <span>영업정산 NO <b>{pinId}</b> 한 줄만 보는 중(다른 화면의 링크로 열림)</span>
          <button className="btn-ghost ml-auto py-0.5 text-xs" onClick={() => navigate('/result-req')}>풀기 — 이 달 전체 보기</button>
        </div>
      )}

      <MonthSearchHint
        q={q}
        mon={mon}
        months={months}
        onPick={(m) => { setMon(m); setPage(0); }}
      />

      {error && <ErrorBox message={error} />}

      {editing && (
        <Modal>
          <ReqForm
            row={editing}
            driveMon={mon}
            isAdmin={isAdmin}
            onClose={() => setEditing(null)}
            onSaved={() => { setEditing(null); setNonce((n) => n + 1); }}
          />
        </Modal>
      )}

      <DataTable
        tableId="settle.resultReq"
        columns={columns}
        rows={rows}
        loading={loading}
        onRowClick={canEdit ? (r) => setEditing(r) : undefined}
        rowClass={(r) => (pinId !== null && Number(r.result_req_id) === pinId ? 'bg-amber-50' : '')}
      />
      <Pager page={page} setPage={setPage} rowCount={rows.length} pageSize={PAGE} loading={loading} setPageSize={setPAGE} baseSize={50} />
    </div>
  );
}
